import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Grid, CircularProgress, Alert, Card, CardContent, Tabs, Tab, Drawer } from '@mui/material';
import RouteDetailPanel from './RouteDetail';
import KPICard from '../../components/common/KPICard';
import DateRangePicker, { getDefaultDateRange, formatDateRange } from '../../components/common/DateRangePicker';
import ExportButton from '../../components/common/ExportButton';
import RevenueChart from '../../components/charts/RevenueChart';
import FuelChart from '../../components/charts/FuelChart';
import DataTable from '../../components/common/DataTable';
import {
  useOverview,
  useTrailerEconomics,
  useRevenueByPeriod,
  useCosts,
  useRouteEconomics,
  useDriverEconomics,
  useCustomerEconomics,
  useOrderEconomics,
  useCostCategories,
  useOperationalKPIs,
} from '../../hooks/useApi';
import type { DateRange, TrailerEconomics, RouteEconomics, OrderEconomics, CustomerEconomics } from '../../types';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value: number, decimals = 0) => {
  return new Intl.NumberFormat('da-DK', {
    maximumFractionDigits: decimals,
  }).format(value);
};

// Vis beløb i tabelcelle, eller "—" hvis 0 / null
const formatCurrencyOrDash = (v: unknown): string => {
  const n = v as number;
  if (!n) return '—';
  return formatCurrency(n);
};

const trailerColumns = [
  { id: 'trailerNumber', label: 'Køretøj', minWidth: 100 },
  { id: 'revenue', label: 'Omsætning', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
  { id: 'totalCosts', label: 'Omkostninger', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
  { id: 'productSalesCost', label: 'Varekostpris', align: 'right' as const,
    format: (v: unknown) => (
      <Typography component="span" sx={{ color: (v as number) > 0 ? 'text.primary' : 'text.disabled' }}>
        {formatCurrencyOrDash(v)}
      </Typography>
    ),
  },
  { id: 'profit', label: 'Profit', align: 'right' as const, format: (v: unknown, row: TrailerEconomics) => (
    <Typography component="span" sx={{ color: row.profit >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
      {formatCurrency(v as number)}
    </Typography>
  )},
  { id: 'kilometers', label: 'Kilometer', align: 'right' as const, format: (v: unknown) => formatNumber(v as number) },
  { id: 'revenuePerKm', label: 'DKK/km', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
];

const routeColumns = [
  { id: 'date', label: 'Dato', minWidth: 90 },
  { id: 'trailerNumber', label: 'Køretøj', minWidth: 90 },
  { id: 'driverNames', label: 'Chauffør', minWidth: 120 },
  { id: 'route', label: 'Rute', minWidth: 140, format: (_: unknown, row: RouteEconomics) => `${row.firstCity || '?'} → ${row.lastCity || '?'}` },
  { id: 'cargoCount', label: 'Ordrer', align: 'right' as const },
  { id: 'revenue', label: 'Omsætning', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
  { id: 'productSalesCost', label: 'Varekostpris', align: 'right' as const,
    format: (v: unknown) => (
      <Typography component="span" sx={{ color: (v as number) > 0 ? 'text.primary' : 'text.disabled' }}>
        {formatCurrencyOrDash(v)}
      </Typography>
    ),
  },
  { id: 'profit', label: 'Profit', align: 'right' as const, format: (v: unknown, row: RouteEconomics) => (
    <Typography component="span" sx={{ color: row.profit >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
      {formatCurrency(v as number)}
    </Typography>
  )},
  { id: 'kilometers', label: 'Km', align: 'right' as const, format: (v: unknown) => formatNumber(v as number) },
  { id: 'hours', label: 'Timer', align: 'right' as const, format: (v: unknown) => formatNumber(v as number, 1) },
  { id: 'revenuePerKm', label: 'DKK/km', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
];

const driverColumns = [
  { id: 'driverName', label: 'Chauffør', minWidth: 150 },
  { id: 'routeCount', label: 'Ruter', align: 'right' as const },
  { id: 'cargoCount', label: 'Ordrer', align: 'right' as const },
  { id: 'revenue', label: 'Omsætning', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
  { id: 'costRuleCosts', label: 'Omkostninger', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
  { id: 'kilometers', label: 'Km', align: 'right' as const, format: (v: unknown) => formatNumber(v as number) },
  { id: 'hours', label: 'Timer', align: 'right' as const, format: (v: unknown) => formatNumber(v as number, 1) },
  { id: 'revenuePerKm', label: 'DKK/km', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
];

const customerColumns = [
  { id: 'customerName', label: 'Kunde', minWidth: 180 },
  { id: 'customerNumber', label: 'Kundenr.', minWidth: 100 },
  { id: 'cargoCount', label: 'Ordrer', align: 'right' as const },
  { id: 'revenue', label: 'Omsætning', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
  { id: 'productSalesCost', label: 'Varekostpris', align: 'right' as const,
    format: (v: unknown) => (
      <Typography component="span" sx={{ color: (v as number) > 0 ? 'text.primary' : 'text.disabled' }}>
        {formatCurrencyOrDash(v)}
      </Typography>
    ),
  },
  { id: 'profit', label: 'Fortjeneste', align: 'right' as const, format: (v: unknown, row: CustomerEconomics) => (
    <Typography component="span" sx={{ color: row.profit >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
      {formatCurrency(v as number)}
    </Typography>
  )},
];

const orderColumns = [
  { id: 'date', label: 'Dato', minWidth: 90 },
  { id: 'cargoRef', label: 'Reference', minWidth: 100 },
  { id: 'customerName', label: 'Kunde', minWidth: 150 },
  { id: 'route', label: 'Rute', minWidth: 140, format: (_: unknown, row: OrderEconomics) => `${row.firstCity || '?'} → ${row.lastCity || '?'}` },
  { id: 'trailerNumber', label: 'Køretøj', minWidth: 80 },
  { id: 'revenue', label: 'Omsætning', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
  { id: 'allocatedCost', label: 'Omkostninger', align: 'right' as const, format: (v: unknown) => formatCurrency(v as number) },
  { id: 'productSalesCost', label: 'Varekostpris', align: 'right' as const,
    format: (v: unknown) => (
      <Typography component="span" sx={{ color: (v as number) > 0 ? 'text.primary' : 'text.disabled' }}>
        {formatCurrencyOrDash(v)}
      </Typography>
    ),
  },
  { id: 'profit', label: 'Profit', align: 'right' as const, format: (v: unknown, row: OrderEconomics) => (
    <Typography component="span" sx={{ color: row.profit >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
      {formatCurrency(v as number)}
    </Typography>
  )},
  { id: 'allocationPct', label: 'Andel', align: 'right' as const, format: (v: unknown) => `${formatNumber(v as number, 1)}%` },
];

export default function AnalyticsOverview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [selectedRoute, setSelectedRoute] = useState<{ trailerId: string; routeId: string } | null>(null);
  const activeTab = Number(searchParams.get('tab') || '0');
  const setActiveTab = (tab: number) => {
    setSearchParams(tab === 0 ? {} : { tab: String(tab) }, { replace: true });
  };
  const { from, to } = formatDateRange(dateRange);

  // Oversigt tab data (always loaded)
  const { data: overview, isLoading: isLoadingOverview, error: overviewError } = useOverview(from, to);
  const { data: economicsData, isLoading: isLoadingEconomics } = useTrailerEconomics(from, to);
  const { data: revenueData, isLoading: isLoadingRevenue } = useRevenueByPeriod(from, to, 'day');
  const { data: costs } = useCosts({ from, to });
  const { data: categories } = useCostCategories();

  const categoryLabels: Record<string, string> = {};
  categories?.forEach(c => { categoryLabels[c.key] = c.name; });

  // Lazy-loaded tab data
  const { data: routeData, isLoading: isLoadingRoutes } = useRouteEconomics(
    activeTab === 1 ? from : '', activeTab === 1 ? to : ''
  );
  const { data: driverData, isLoading: isLoadingDrivers } = useDriverEconomics(
    activeTab === 2 ? from : '', activeTab === 2 ? to : ''
  );
  const { data: customerData, isLoading: isLoadingCustomers } = useCustomerEconomics(
    activeTab === 3 ? from : '', activeTab === 3 ? to : ''
  );
  const { data: orderData, isLoading: isLoadingOrders } = useOrderEconomics(
    activeTab === 4 ? from : '', activeTab === 4 ? to : ''
  );
  const { data: operationalData, isLoading: isLoadingOperational } = useOperationalKPIs(
    activeTab === 5 ? from : '', activeTab === 5 ? to : ''
  );

  // Group costs by category
  const costsByCategory = costs?.reduce((acc, cost) => {
    const existing = acc.find(c => c.category === cost.category);
    if (existing) {
      existing.total += cost.amount;
    } else {
      acc.push({ category: cost.category, total: cost.amount });
    }
    return acc;
  }, [] as Array<{ category: string; total: number }>) || [];

  if (overviewError) {
    return (
      <Box>
        <Alert severity="error">
          Kunne ikke hente data. Kontroller din DORA API-forbindelse.
        </Alert>
      </Box>
    );
  }

  const isLoading = isLoadingOverview || isLoadingEconomics;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Økonomi</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <ExportButton from={from} to={to} />
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Oversigt" />
        <Tab label="Ruteregnskab" />
        <Tab label="Chaufførregnskab" />
        <Tab label="Kunderegnskab" />
        <Tab label="Ordreregnskab" />
        <Tab label="Drift" />
      </Tabs>

      {/* Tab 0: Oversigt (existing content) */}
      {activeTab === 0 && (
        <>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : overview ? (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} md={overview.totalProductSalesCost > 0 ? 2.4 : 3}>
                  <KPICard
                    title="Total Omsætning"
                    value={formatCurrency(overview.totalRevenue)}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={6} md={overview.totalProductSalesCost > 0 ? 2.4 : 3}>
                  <KPICard
                    title="Total Omkostninger"
                    value={formatCurrency(overview.totalFuelCost + overview.totalManualCosts)}
                    color="warning"
                  />
                </Grid>
                {overview.totalProductSalesCost > 0 && (
                  <Grid item xs={6} md={2.4}>
                    <KPICard
                      title="Varekostpris"
                      value={formatCurrency(overview.totalProductSalesCost)}
                      color="warning"
                    />
                  </Grid>
                )}
                <Grid item xs={6} md={overview.totalProductSalesCost > 0 ? 2.4 : 3}>
                  <KPICard
                    title="Total Profit"
                    value={formatCurrency(overview.totalProfit)}
                    color={overview.totalProfit >= 0 ? 'success' : 'error'}
                  />
                </Grid>
                <Grid item xs={6} md={overview.totalProductSalesCost > 0 ? 2.4 : 3}>
                  <KPICard
                    title="Gns. DKK/km"
                    value={formatCurrency(overview.averageRevenuePerKm)}
                    color="info"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} md={8}>
                  {isLoadingRevenue ? (
                    <CircularProgress />
                  ) : revenueData ? (
                    <RevenueChart data={revenueData} title="Omsætning over tid" />
                  ) : null}
                </Grid>
                <Grid item xs={12} md={4}>
                  {costsByCategory.length > 0 ? (
                    <FuelChart data={costsByCategory} title="Omkostningsfordeling" categoryLabels={categoryLabels} />
                  ) : (
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Omkostningsfordeling
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ingen manuelle omkostninger registreret for denne periode.
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Grid>
              </Grid>

              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Vognregnskab
                  </Typography>
                  {economicsData?.trailers ? (
                    <DataTable
                      columns={trailerColumns}
                      data={economicsData.trailers}
                      rowKey="trailerId"
                      compact
                      searchable={false}
                    />
                  ) : null}
                </CardContent>
              </Card>
            </>
          ) : null}
        </>
      )}

      {/* Tab 1: Ruteregnskab */}
      {activeTab === 1 && (
        <>
          {isLoadingRoutes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : routeData?.routes ? (
            <DataTable
              columns={routeColumns}
              data={routeData.routes}
              rowKey="routeId"
              searchPlaceholder="Søg rute..."
              onRowClick={(row) => setSelectedRoute({ trailerId: row.trailerId, routeId: row.routeId })}
            />
          ) : null}
        </>
      )}

      {/* Tab 2: Chaufførregnskab */}
      {activeTab === 2 && (
        <>
          {isLoadingDrivers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : driverData?.drivers ? (
            <DataTable
              columns={driverColumns}
              data={driverData.drivers}
              rowKey="driverId"
              searchPlaceholder="Søg chauffør..."
            />
          ) : null}
        </>
      )}

      {/* Tab 3: Kunderegnskab */}
      {activeTab === 3 && (
        <>
          {isLoadingCustomers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : customerData?.customers ? (
            <DataTable
              columns={customerColumns}
              data={customerData.customers}
              rowKey="customerNumber"
              searchPlaceholder="Søg kunde..."
            />
          ) : null}
        </>
      )}

      {/* Tab 4: Ordreregnskab */}
      {activeTab === 4 && (
        <>
          {isLoadingOrders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : orderData?.orders ? (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                  <KPICard
                    title="Total Omsætning"
                    value={formatCurrency(orderData.orders.reduce((s: number, o: OrderEconomics) => s + o.revenue, 0))}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <KPICard
                    title="Total Omkostninger"
                    value={formatCurrency(orderData.orders.reduce((s: number, o: OrderEconomics) => s + o.allocatedCost, 0))}
                    color="warning"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <KPICard
                    title="Total Profit"
                    value={formatCurrency(orderData.orders.reduce((s: number, o: OrderEconomics) => s + o.profit, 0))}
                    color={orderData.orders.reduce((s: number, o: OrderEconomics) => s + o.profit, 0) >= 0 ? 'success' : 'error'}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <KPICard
                    title="Antal ordrer"
                    value={formatNumber(orderData.orders.length)}
                    color="info"
                  />
                </Grid>
              </Grid>
              <DataTable
                columns={orderColumns}
                data={orderData.orders}
                rowKey="cargoId"
                searchPlaceholder="Søg ordre..."
              />
            </>
          ) : null}
        </>
      )}

      {/* Tab 5: Drift */}
      {activeTab === 5 && (
        <>
          {isLoadingOperational ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : operationalData ? (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} md={2}>
                  <KPICard
                    title="Leveret ton"
                    value={`${formatNumber(operationalData.deliveredTons, 1)} t`}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KPICard
                    title="Containere"
                    value={formatNumber(operationalData.containerCount)}
                    color="info"
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KPICard
                    title="Krantimer"
                    value={`${formatNumber(operationalData.craneHours, 1)} t`}
                    color="warning"
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KPICard
                    title="Slamsuger-timer"
                    value={`${formatNumber(operationalData.sludgeHours, 1)} t`}
                    color="info"
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KPICard
                    title="Antal ordrer"
                    value={formatNumber(operationalData.totalCargos)}
                    subtitle={`${formatNumber(operationalData.totalStops)} stop`}
                    color="success"
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KPICard
                    title="Læssemeter"
                    value={`${formatNumber(operationalData.totalLoadmeters, 1)} lm`}
                    color="primary"
                  />
                </Grid>
              </Grid>
              {operationalData.byWeek.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      Uge for uge
                    </Typography>
                    <DataTable
                      columns={[
                        { id: 'week', label: 'Uge', minWidth: 100 },
                        { id: 'cargoCount', label: 'Ordrer', align: 'right' as const, format: (v: unknown) => formatNumber(v as number) },
                        { id: 'deliveredTons', label: 'Leveret ton', align: 'right' as const, format: (v: unknown) => `${formatNumber(v as number, 1)} t` },
                        { id: 'containerCount', label: 'Containere', align: 'right' as const, format: (v: unknown) => formatNumber(v as number) },
                        { id: 'craneHours', label: 'Krantimer', align: 'right' as const, format: (v: unknown) => `${formatNumber(v as number, 1)} t` },
                      ]}
                      data={operationalData.byWeek}
                      rowKey="week"
                    />
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Alert severity="info">Ingen driftsdata for perioden.</Alert>
          )}
        </>
      )}

      {/* Route detail drawer */}
      <Drawer
        anchor="right"
        open={!!selectedRoute}
        onClose={() => setSelectedRoute(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}
      >
        {selectedRoute && (
          <RouteDetailPanel
            trailerId={selectedRoute.trailerId}
            routeId={selectedRoute.routeId}
            onClose={() => setSelectedRoute(null)}
          />
        )}
      </Drawer>
    </Box>
  );
}
